// src/pages/PageMenu/PurchasesMenu.jsx
// قائمة المشتريات — بالتصميم الموحّد (MenuKit) مع دعم الترجمة

import React, { useState, useEffect } from "react";
import axios from "../../axios";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import { MenuPage, useMenuT } from "../../components/Menu/MenuKit";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReplayIcon from "@mui/icons-material/Replay";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GroupIcon from "@mui/icons-material/Group";
import BarChartIcon from "@mui/icons-material/BarChart";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowCircleUpOutlinedIcon from "@mui/icons-material/ArrowCircleUpOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";

// أنواع فواتير المشتريات: بالرقم أولًا ثم بالاسم (احتياطي لبيانات قديمة)
const PURCHASE_TYPES = {
  2: { nameKey: "purchasesMenu.purchaseInvoice", nameFallback: "فاتورة شراء", names: ["شراء", "فاتورة شراء"], icon: <ReceiptLongIcon />, color: "#E11D48", perm: "INVOICE_PURCHASE_CREATE" },
  4: { nameKey: "purchasesMenu.purchaseReturn", nameFallback: "مرتجع مشتريات", names: ["مرتجع مشتريات", "فاتورة مرتجع مشتريات"], icon: <ReplayIcon />, color: "#D97706", perm: "INVOICE_PURCHASE_RETURN_CREATE" },
};
const typeIdOf = (t) => {
  const id = Number(t.invoiceTypeId);
  if (PURCHASE_TYPES[id]) return id;
  const name = (t.name || "").trim();
  return Number(Object.keys(PURCHASE_TYPES).find(k => PURCHASE_TYPES[k].names.includes(name))) || null;
};

export default function PurchasesMenu() {
  const { tr } = useMenuT();
  const { hasPermission, hasAnyPermission } = useAuth();
  const companyId = parseInt(localStorage.getItem("companyId"), 10) || null;

  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [loading, setLoading] = useState(!!companyId);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!companyId) { setLoadError(tr("purchasesMenu.companyIdNotFound", "لم يتم تحديد الشركة")); return undefined; }
    let alive = true;
    axios.get("/InvoiceTypes", { params: { companyId } })
      .then(res => alive && setInvoiceTypes(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error("❌ فشل في تحميل أنواع الفواتير:", err);
        if (alive) setLoadError(tr("purchasesMenu.failedToLoadInvoiceTypes", "تعذّر تحميل أنواع الفواتير"));
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [companyId]); // tr ثابت عمليًا

  const can = (key) => !!PERMISSIONS[key] && hasPermission(PERMISSIONS[key]);
  const canAny = (...keys) => hasAnyPermission(keys);

  // بطاقات أنواع الفواتير (بدون تكرار، وبترتيب ثابت)
  const seen = new Set();
  const typeCards = invoiceTypes
    .map(type => ({ type, id: typeIdOf(type) }))
    .filter(({ id }) => id && !seen.has(id) && seen.add(id) && can(PURCHASE_TYPES[id].perm))
    .sort((a, b) => a.id - b.id)
    .map(({ type, id }) => ({ key: `type-${id}`, ...PURCHASE_TYPES[id], link: `/invoices/new/${type.invoiceTypeId}` }));

  const sections = [
    {
      key: "operations",
      titleKey: "purchasesMenu.operationsSection", titleFallback: "العمليات",

      icon: <BoltOutlinedIcon />, color: "#E11D48", loading,
      items: [
        ...typeCards,
        // بطاقة واحدة لسند الصرف (كانت مكررة بصلاحيتين)
        canAny("Bond.Payment.Create") && { key: "supplierPayment", nameKey: "purchasesMenu.supplierPayment", nameFallback: "سند صرف لمورد", icon: <ArrowCircleUpOutlinedIcon />, color: "#BE123C", link: "/vouchers/supplier-payment" },
        can("SUPPLIER_CREATE") && { key: "addSupplier", nameKey: "purchasesMenu.addSupplier", nameFallback: "إضافة مورد", icon: <LocalShippingIcon />, color: "#4F46E5", link: "/Supplier/new" },
        can("SUPPLIER_VIEW") && { key: "suppliers", nameKey: "purchasesMenu.suppliersList", nameFallback: "قائمة الموردين", icon: <GroupIcon />, color: "#6366F1", link: "/Supplier" },
      ],
    },
    {
      key: "reports",
      titleKey: "purchasesMenu.reportsSection", titleFallback: "تقارير المشتريات",
      icon: <InsightsOutlinedIcon />, color: "#0891B2",
      items: [
        can("INVOICE_PURCHASE_VIEW") && { key: "allPurchases", nameKey: "purchasesMenu.allPurchaseInvoices", nameFallback: "فواتير المشتريات", icon: <ListAltIcon />, color: "#2563EB", link: "/reports/all-invoices?direction=1&invoiceTypeId=2" },
        can("INVOICE_PURCHASE_RETURN_VIEW") && { key: "allReturns", nameKey: "purchasesMenu.allPurchaseReturns", nameFallback: "مرتجعات المشتريات", icon: <AssignmentReturnOutlinedIcon />, color: "#D97706", link: "/reports/all-invoices?direction=1&invoiceTypeId=4" },
        canAny("Report.View") && { key: "byDay", nameKey: "purchasesMenu.dailyPurchaseReport", nameFallback: "المشتريات حسب اليوم", icon: <CalendarTodayIcon />, color: "#059669", link: "/reports/purchase-by-day" },
        canAny("Report.View") && { key: "itemsSum", nameKey: "purchasesMenu.aggregatedItemsPurchaseReport", nameFallback: "مشتريات الأصناف — تجميعي", icon: <SummarizeIcon />, color: "#0D9488", link: "/reports/purchase-items-summary" },
        canAny("Report.View") && { key: "itemsDet", nameKey: "purchasesMenu.detailedItemsPurchaseReport", nameFallback: "مشتريات الأصناف — تفصيلي", icon: <BarChartIcon />, color: "#10B981", link: "/reports/purchase-items-detail" },
      ],
    },
  ];

  return (
    <MenuPage
      titleKey="purchasesMenu.dashboardTitle" titleFallback="المشتريات"
      icon={<ShoppingCartOutlinedIcon />}
      sections={sections}
      warning={loadError ? `${loadError} — ${tr("menu.otherScreensWork", "تظهر بقية الشاشات بشكل طبيعي")}` : null}
    />
  );
}